/**
 * Analytics request schemas — SPLIT OUT of the master schemas.ts so the
 * analytics owner (Frank) can self-merge dashboard changes (e.g. a new
 * `dashboard` enum value or filter) without touching the app-wide schemas.ts.
 * This file is inside the analytics CODEOWNERS carve-out; schemas.ts is not.
 *
 * Keep ANALYTICS-only schemas here. Anything that validates hub-core data
 * (users/config/statements/pay/…) stays in schemas.ts (reviewed).
 */
import { z } from 'zod';
import { ISODateSchema } from './schemas.js';
/** Analytics admin: grant/revoke a viewer's page access from the dashboard. */
export const AnalyticsSetUserPagesSchema = z.object({
    targetEmail: z.string().trim().toLowerCase().email(),
    pages: z.array(z.string().trim().min(1).max(40)).max(40),
});
/** Analytics dashboards (BigQuery aggregates). Daily rows between from/to. */
export const AnalyticsDashboardSchema = z
    .object({
    dashboard: z.enum([
        'traffic', 'channels', 'channelsCountry', 'breakdown', 'pages', 'pageLookup', 'catalog', 'catalogProducts', 'catalogInsights', 'catalogScoreDist',
        'catalogLookup', 'catalogClients', 'catalogClientDetail', 'catalogClientLookup', 'catalogTopByMarket', 'catalogSources',
        'articles', 'articleItems', 'articleReferrers', 'articleSources', 'articleOwners', 'articleTags', 'articleAll',
        'articleLookup',
        'articleOwnersInsights', 'articleTagsInsights', 'articleRegions', 'articleOwnersTrend', 'articleTagsTrend',
        'articleOwnerSearch', 'articleTagSearch', 'articleOwnerArticles', 'articleTagArticles',
        'articleRefloats', 'articleRefloatDetail',
        'projects', 'projectItems', 'projectOwners', 'projectCategories', 'projectCountries', 'projectRankedBreakdown', 'projectAll', 'projectLookup',
        'projectRefloats', 'projectRefloatDetail', 'projectSources',
        'audioSummary', 'audioEvents',
        'membershipOverview', 'membershipComparison', 'membershipMarketComparison',
        'newsletter', 'newsletterUrlLookup', 'newsletterCampaignList', 'newsletterUrlList',
        'socialV2', 'socialV2Posts', 'socialV2PostTimeline', 'socialV2Audience',
        'poPm', 'poPmEvents', 'poPmInsights',
        'engagementLab', 'engagementRecos', 'adScore',
        'competitorsChrome',
        'competitorsChromeCountry',
        'competitorsPublishing',
        'userInsightsEvents', 'userInsightsFollows', 'userInsightsOverview', 'userInsightsContent', 'userInsightsFlow',
    ]),
    from: ISODateSchema,
    to: ISODateSchema,
    /** Optional site filter (empty/absent = all sites). */
    sites: z.array(z.string().trim().min(1)).max(50).optional(),
    /** Channels dashboard only: filter + grouping. */
    devices: z.array(z.string().trim().min(1)).max(20).optional(),
    channels: z.array(z.string().trim().min(1)).max(30).optional(),
    groupBy: z.enum(['channel', 'device', 'source', 'social']).optional(),
    /** channelsCountry only: the single row (channel/device/source/platform
     *  value) to fetch a top-10-countries breakdown for. */
    groupKey: z.string().trim().min(1).max(200).optional(),
    /** Include flagged bot/crawler traffic (default false = humans only). */
    includeBots: z.boolean().optional(),
    /** Traffic subpage only: restrict to visits with a non-null user_id
     *  (default false = all users, registered + anonymous). */
    registeredOnly: z.boolean().optional(),
    /** User insights only: restrict to users with an active Piano subscription (the group 'sub', a subset of the registered users). */
    subscribersOnly: z.boolean().optional(),
    /** Traffic dashboard: server-side bucket size (users need HLL merges per
     *  bucket - can't be regrouped client-side). Default 'day'. */
    granularity: z.enum(['day', 'week', 'month']).optional(),
    /** Traffic + breakdown: traffic-source filter (default 'all'). Split from
     *  a single combined "Origin" filter into Source + Device (15 Sep) so
     *  they compose — 'mobile'/'desktop' moved to the new `device` field. */
    origin: z.enum(['all', 'organic']).optional(),
    /** Traffic + breakdown: device-type filter (default 'all'), independent
     *  of `origin` — see the note above. */
    device: z.enum(['all', 'mobile', 'desktop']).optional(),
    /** Traffic + breakdown: region filter (default 'all'/'global'), extracted
     *  from a /{region}/ pagepath segment — mirrors the Looker Studio report's
     *  own region breakdown. Independent of origin/device, same "always
     *  concrete, never omit" rule. */
    region: z.enum(['all', 'africa', 'europe', 'north-america', 'south-america', 'asia', 'oceania']).optional(),
    /** Breakdown dashboard: which dimension to rank. */
    dim: z.enum(['country', 'city', 'browser', 'os', 'screen', 'device']).optional(),
    /** Breakdown: restrict city rows to one country (deep dive). */
    parent: z.string().trim().max(120).optional(),
    /** Breakdown: number of values to return (default 10, max 100;
     *  engagementLab uses up to 300 for a stable score cohort). */
    limit: z.number().int().min(1).max(300).optional(),
    /** Catalogue/Articles/Channels/Traffic+breakdown: language filter (path prefix, e.g. 'en'/'es'); absent = all. */
    lang: z.string().trim().max(5).optional(),
    /** Catalogue: Neufert category filter, one per taxonomy tier; empty/absent
     *  = no filter at that tier. Applies wherever the underlying data is tied
     *  to a single product (see web/src/analytics/CLAUDE.md "Category filter"). */
    catL1: z.string().trim().max(120).optional(),
    catL2: z.string().trim().max(120).optional(),
    catL3: z.string().trim().max(120).optional(),
    /** Catalogue market filter: the regional catalogue a product is listed in (US, Chile, Mexico, ...), from product_company.market_name. */
    market: z.string().trim().max(60).optional(),
    /** pageLookup only: exact pagepath to look up (e.g. '/990866/glass-house-…').
     *  Absent = no lookup performed, pageLookup comes back null. */
    lookupPath: z.string().trim().min(1).max(500).optional(),
    /** articleLookup/projectLookup/catalogLookup/catalogClientLookup: the numeric id to look up (extracted
     *  client-side from a pasted archdaily.com URL, or entered directly).
     *  socialMediaPostTimeline: the post's ID/permalink (reused as-is — a
     *  social post's "id" can be a full Instagram/Facebook URL, not numeric,
     *  hence the longer max than the other three lookups need). */
    aid: z.string().trim().max(300).optional(),
    /** articleLookup/projectLookup/catalogLookup/catalogClientLookup: an optional custom window,
     *  independent of the page's own from/to (which the lookup otherwise
     *  ignores — see dashboards.js). Both must be present to activate;
     *  absent/either-missing = all-time, the lookup's default. */
    lookupFrom: ISODateSchema.optional(),
    lookupTo: ISODateSchema.optional(),
    /** articleRefloats only: "refloated between X and Y" — matches ANY of
     *  an article's refloat dates, not just the earliest one. Both must be
     *  present to activate; absent/either-missing = no date filter. */
    refloatFrom: ISODateSchema.optional(),
    refloatTo: ISODateSchema.optional(),
    /** articles/articleItems/articleAll/articleOwnersInsights/
     *  articleTagsInsights/articleOwnersTrend/articleTagsTrend: restrict to
     *  articles whose real publish date (content_meta.published_at) falls
     *  within from/to, and count only their traffic within that same range
     *  — "how did this period's NEW content do", not "how did all content
     *  do this period". Absent/false = today's default behavior. */
    newPubsOnly: z.boolean().optional(),
    /** Articles page-wide "Tag group" filter: restrict to articles carrying
     *  ANY of these real nimrod__articles tag strings (a named group,
     *  resolved client-side from web/src/analytics/articleTagGroups.ts). Composes
     *  with newPubsOnly. Absent/empty = no tag restriction. Refloats and
     *  articleLookup deliberately ignore it. */
    tagGroup: z.array(z.string().trim().min(1).max(120)).max(60).optional(),
    /** catalogProducts / articleItems: ranking metric. socialMediaPosts:
     *  which metric column to sort the post table by (default 'reach'). */
    sort: z.enum(['views', 'single', 'gallery', 'duration', 'reach', 'impressions', 'engagement', 'likes', 'comments', 'shares', 'linkClicks', 'published', 'saves', 'clicks', 'videoViews', 'spend']).optional(),
    /** socialMediaPosts: sort direction for `sort` above (default 'desc'). */
    sortDir: z.enum(['asc', 'desc']).optional(),
    /** Audio dashboards: filter by the GA event category/label/specific_action
     *  (dim2)/audio_language/raw action string. Empty/absent = no filter. */
    audioCategory: z.array(z.string().trim().min(1)).max(20).optional(),
    audioLabel: z.array(z.string().trim().min(1)).max(20).optional(),
    audioSpecificAction: z.array(z.string().trim().min(1)).max(20).optional(),
    audioLang: z.array(z.string().trim().min(1)).max(20).optional(),
    audioAction: z.array(z.string().trim().min(1)).max(20).optional(),
    /** audioEvents only: 'single' (default) = real article-page URLs;
     *  'other' = gallery sub-pages + non-article audio-widget pages. */
    urlKind: z.enum(['single', 'other']).optional(),
    /** articleAll: pagination offset (default 0). Also used by
     *  articleOwnersInsights/articleTagsInsights for their own page size. */
    offset: z.number().int().min(0).max(100_000).optional(),
    /** List branches (articleAll first): return every matching row up to the Export all cap, in sort order, instead of one page. */
    exportAll: z.boolean().optional(),
    /** articleOwnersInsights/articleTagsInsights: scope to exactly these
     *  names (the per-tab author/tag filter) instead of paginating the full
     *  ranked list. articleOwnersTrend/articleTagsTrend: the fixed set of
     *  names to chart (always required there — no default cohort). */
    names: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
    /** articleOwnerSearch/articleTagSearch: the typed search text for the
     *  per-tab combobox (case-insensitive substring match); absent/empty =
     *  browse the full list. socialMediaPosts: same case-insensitive
     *  substring match against a post's title OR link. */
    q: z.string().trim().max(120).optional(),
    /** Membership dashboards: filter by company_name / membership plan name
     *  (plan name 'null' matches accounts with no active plan). Empty/absent = all. */
    companies: z.array(z.string().trim().min(1).max(200)).max(500).optional(),
    memberships: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
    /** Membership dashboards: filter by the account's market (US, Chile, ...); 'null' matches accounts with no market. Empty/absent = all. */
    membershipMarkets: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
    /** membershipComparison only: the source report's per-section "Comparison"
     *  dropdown — narrows one group (Exposure/Pageviews/Interactions) to a
     *  single dimension_name instead of summing every dimension in that
     *  group (re-ranks the top-9/Top/Bottom lists off the filtered value).
     *  Empty/absent per group = unchanged default (sum of all dimensions). */
    membershipDimExposure: z.string().trim().max(120).optional(),
    membershipDimPageviews: z.string().trim().max(120).optional(),
    membershipDimInteractions: z.string().trim().max(120).optional(),
    /** Newsletter dashboard: brand filter (empty/absent = all brands). */
    nlBrands: z.array(z.string().trim().min(1).max(20)).max(20).optional(),
    /** Newsletter dashboard: ESP source filter (default 'moengage', the
     *  current live platform; 'all' includes the legacy mailchimp/mailup rows). */
    nlSource: z.enum(['all', 'moengage', 'mailchimp', 'mailup']).optional(),
    /** newsletterCampaignList / newsletterUrlList: sort column (a key from the server's whitelist) and direction. */
    nlSort: z.string().trim().min(1).max(24).optional(),
    nlDir: z.enum(['asc', 'desc']).optional(),
    /** newsletterUrlLookup: the exact destination URL to look up (as stored
     *  on daily_newsletter_links.link_url, protocol included) — respects the
     *  SAME from/to/nlSource/nlBrands filters as the 'newsletter' dashboard
     *  (unlike Articles/Projects/Catalogue's URL lookups, which are always
     *  all-time; confirmed with Frank for this page specifically). Absent =
     *  no lookup performed. */
    lookupUrl: z.string().trim().min(1).max(2000).optional(),
    /** Social Media dashboard: brand filter — MULTI-select (empty/absent =
     *  all brands), so a viewer can exclude just one brand (e.g. Architonic's
     *  Pinterest product-pin volume) while keeping the rest, not only narrow
     *  to a single one. Same shape/semantics as Newsletter's `nlBrands`. */
    brands: z.array(z.enum(['ArchDaily', 'Architonic', 'designboom', 'Other', 'Milan Design Week'])).max(5).optional(),
    /** socialV2Posts / socialV2: restrict to these content formats (reel, story, carousel, post, video, short, live, pin, ad). Empty/absent = all. */
    socialFormats: z.array(z.string().trim().min(1).max(20)).max(12).optional(),
    /** socialMediaPosts: Organic/Paid filter (default 'all'), derived from
     *  source_name (LIKE '%Paid%' OR '%Ads%' = paid, everything else organic). */
    postType: z.enum(['all', 'organic', 'paid']).optional(),
    /** PO/PM dashboards: category filter (chip multi-select, low cardinality)
     *  + action/label free-text substring filters — action's cardinality
     *  (raw GTM trigger ids) is too high for a chip list, so it mirrors the
     *  source Looker tab's own free-text filter boxes instead. */
    poPmCategory: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
    poPmAction: z.string().trim().max(200).optional(),
    poPmLabel: z.string().trim().max(200).optional(),
    /** poPmInsights only: Country/Device category/external_user/Account_id
     *  filters, matching the source Looker report's own filter row. Country
     *  and Account_id apply everywhere except the KPI tiles/trend (GA4's
     *  distinct-user metrics can't be cleanly filtered by page-path-derived
     *  Account_id at that grain — see shared PoPmInsightsBreakdownRow's doc
     *  comment). Empty/absent = no filter. */
    poPmCountry: z.string().trim().max(120).optional(),
    poPmDevice: z.string().trim().max(40).optional(),
    poPmExternalUser: z.string().trim().max(40).optional(),
    poPmAccountId: z.string().trim().max(20).optional(),
    /** engagementLab/engagementRecos: which cohort — 'article' (default),
     *  'project', 'catalogue', 'newsletter', or 'socialMedia'. Projects read
     *  daily_projects_item + their own semantics/recos tables and taxonomy
     *  (no originality concept — see classifyProjectSemantics). Catalogue,
     *  Newsletter, and Social Media have NO semantic layer at all (Catalogue:
     *  Neufert's category taxonomy is already real structured data;
     *  Newsletter: no page text to read — a campaign send isn't a public
     *  page; Social Media: post captions aren't read/classified) —
     *  engagementRecos/classify aren't valid for any of the three, so only
     *  AnalyticsDashboardSchema's target grows the enum here. */
    target: z.enum(['article', 'project', 'catalogue', 'newsletter', 'socialMedia']).optional(),
})
    .refine((v) => v.to >= v.from, { path: ['to'], message: 'End before start.' });
/** Engagement Lab: classify the next batch of top unclassified articles
 *  (hub callable `analyticsSemanticClassify`, admin-gated). */
export const AnalyticsSemanticClassifySchema = z.object({
    limit: z.number().int().min(1).max(40).optional(),
    /** 'article' (default) or 'project' — which cohort + taxonomy to classify. */
    target: z.enum(['article', 'project']).optional(),
});
/** Engagement Lab: build the STABLE recommendation card for one score-bucket
 *  transition (0 = 0s->10s … 8 = 80s->90s). Hub callable `analyticsRecoBuild`. */
export const AnalyticsRecoBuildSchema = z.object({
    transition: z.number().int().min(0).max(8),
    /** 'article' (default) or 'project' — which cohort to build the card from. */
    target: z.enum(['article', 'project']).optional(),
});
/** Ask Data Questions — a chat message. Sent as its own `dashboard: 'ask'`
 *  action (separate from AnalyticsDashboardSchema's BigQuery-dashboard enum,
 *  since this hits a Gemini + query-mapping path, not a single BQ query). */
export const AnalyticsAskSchema = z.object({
    dashboard: z.literal('ask'),
    question: z.string().trim().min(1).max(500),
    /** Short recent history for follow-ups ("and last month?") — capped small,
     *  never persisted server-side. */
    history: z
        .array(z.object({ role: z.enum(['user', 'assistant']), text: z.string().trim().min(1).max(2000) }))
        .max(8)
        .optional(),
});
//# sourceMappingURL=analyticsSchemas.js.map